import { Helmet } from 'react-helmet-async';

// SEO Configuration for all pages
export const seoConfig = {
    siteName: 'Craven Canines',
    siteUrl: 'https://www.cravencanines.com',
    defaultTitle: 'Craven Canines | Professional Mantrailing & Dog Training in Malmesbury, Wiltshire',
    defaultDescription: 'Expert mantrailing sessions and dog training workshops near Malmesbury, Wiltshire. Transform your dog\'s natural abilities with fun, rewarding scent work. Book your session today!',
    defaultKeywords: 'mantrailing, dog training, scent work, Malmesbury, Wiltshire, dog enrichment, canine training, dog workshops',
    defaultImage: 'https://www.cravencanines.com/og-image.jpg',
    twitterHandle: '@cravencanines',
    locale: 'en_GB',
    
    // Business info for LocalBusiness schema
    business: {
        name: 'Craven Canines',
        email: 'infocravencanines@gmail.com',
        addressRegion: 'Wiltshire',
        addressLocality: 'Malmesbury',
        addressCountry: 'GB',
        priceRange: '££',
        latitude: 51.5857,
        longitude: -2.0997
    }
};

// Page-specific SEO configurations
export const pageSEO = {
    home: {
        title: 'Craven Canines | Professional Mantrailing & Dog Training Malmesbury',
        description: 'Discover the joy of mantrailing with your dog in Wiltshire. Expert-led sessions that build confidence, strengthen your bond, and provide mental enrichment. Book now!',
        keywords: 'mantrailing Wiltshire, dog training Malmesbury, scent work classes, dog enrichment activities, canine nose work',
        canonical: '/'
    },
    mantrailing: {
        title: 'Mantrailing Sessions & Workshops | Dog Scent Training Wiltshire',
        description: 'Join our mantrailing workshops near Malmesbury. From beginner introductions to advanced training sessions. All breeds welcome. Limited spots available!',
        keywords: 'mantrailing courses, dog scent training, tracking dogs, mantrailing workshops Wiltshire, nose work classes',
        canonical: '/mantrailing'
    },
    gallery: {
        title: 'Gallery | Dogs in Action at Craven Canines Mantrailing Sessions',
        description: 'See our happy dogs and handlers enjoying mantrailing sessions in the Wiltshire countryside. Real results from real training sessions.',
        keywords: 'mantrailing photos, dog training gallery, scent work pictures, Craven Canines dogs',
        canonical: '/gallery'
    },
    contact: {
        title: 'Contact Craven Canines | Book Dog Training in Wiltshire',
        description: 'Get in touch to book your mantrailing session or ask questions. Sessions available within 20 minutes of Malmesbury. We respond within 24 hours.',
        keywords: 'contact dog trainer Wiltshire, book mantrailing session, Craven Canines enquiry',
        canonical: '/contact'
    },
    faq: {
        title: 'Mantrailing FAQs | Common Questions About Dog Scent Training',
        description: 'Everything you need to know about mantrailing: what to bring, how it works, and if your dog is suitable. Expert answers to your questions.',
        keywords: 'mantrailing FAQ, dog training questions, scent work beginners, mantrailing explained',
        canonical: '/faq'
    },
    giftVouchers: {
        title: 'Gift Vouchers | Give the Gift of Mantrailing Adventures',
        description: 'Perfect gift for dog lovers! Treat someone special to a mantrailing experience in Wiltshire. Digital vouchers delivered instantly.',
        keywords: 'dog training gift voucher, mantrailing gift, dog experience gift, pet gift ideas',
        canonical: '/gift-vouchers'
    },
    cart: {
        title: 'Your Booking | Craven Canines',
        description: 'Complete your mantrailing session booking. Secure checkout with instant confirmation.',
        keywords: 'book mantrailing, dog training checkout',
        canonical: '/cart'
    }
};

// Schema markup generators
export const generateOrganizationSchema = () => ({
    "@context": "https://schema.org",
    "@type": "Organization",
    "name": seoConfig.business.name,
    "url": seoConfig.siteUrl,
    "logo": "https://customer-assets.emergentagent.com/job_1b9f5e6a-070e-4979-a359-5f8d6032745e/artifacts/ta23n7eh_Official%20logo%20%281%29.png",
    "email": seoConfig.business.email,
    "sameAs": [
        "https://www.instagram.com/cravencanines",
        "https://www.facebook.com/cravencanines"
    ]
});

export const generateLocalBusinessSchema = () => ({
    "@context": "https://schema.org",
    "@type": "LocalBusiness",
    "@id": seoConfig.siteUrl,
    "name": seoConfig.business.name,
    "description": "Professional mantrailing and dog training services in Wiltshire",
    "url": seoConfig.siteUrl,
    "email": seoConfig.business.email,
    "logo": "https://customer-assets.emergentagent.com/job_1b9f5e6a-070e-4979-a359-5f8d6032745e/artifacts/ta23n7eh_Official%20logo%20%281%29.png",
    "image": "https://customer-assets.emergentagent.com/job_1b9f5e6a-070e-4979-a359-5f8d6032745e/artifacts/ta23n7eh_Official%20logo%20%281%29.png",
    "address": {
        "@type": "PostalAddress",
        "addressLocality": seoConfig.business.addressLocality,
        "addressRegion": seoConfig.business.addressRegion,
        "addressCountry": seoConfig.business.addressCountry
    },
    "geo": {
        "@type": "GeoCoordinates",
        "latitude": seoConfig.business.latitude,
        "longitude": seoConfig.business.longitude
    },
    "priceRange": seoConfig.business.priceRange,
    "openingHoursSpecification": [
        {
            "@type": "OpeningHoursSpecification",
            "dayOfWeek": ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"],
            "opens": "09:00",
            "closes": "18:00"
        }
    ],
    "areaServed": {
        "@type": "GeoCircle",
        "geoMidpoint": {
            "@type": "GeoCoordinates",
            "latitude": seoConfig.business.latitude,
            "longitude": seoConfig.business.longitude
        },
        "geoRadius": "32000"
    }
});

export const generateServiceSchema = (service) => ({
    "@context": "https://schema.org",
    "@type": "Service",
    "serviceType": service.name,
    "provider": {
        "@type": "LocalBusiness",
        "name": seoConfig.business.name
    },
    "areaServed": {
        "@type": "Place",
        "name": "Wiltshire, UK"
    },
    "description": service.description,
    "offers": {
        "@type": "Offer",
        "price": service.price,
        "priceCurrency": "GBP"
    }
});

export const generateFAQSchema = (faqs) => ({
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faqs.map(faq => ({
        "@type": "Question",
        "name": faq.question,
        "acceptedAnswer": {
            "@type": "Answer",
            "text": faq.answer
        }
    }))
});

export const generateBreadcrumbSchema = (items) => ({
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": items.map((item, index) => ({
        "@type": "ListItem",
        "position": index + 1,
        "name": item.name,
        "item": `${seoConfig.siteUrl}${item.url}`
    }))
});

// SEO Head Component
const SEOHead = ({ 
    title, 
    description, 
    keywords, 
    canonical,
    image,
    type = 'website',
    schema = null,
    noindex = false
}) => {
    const fullTitle = title || seoConfig.defaultTitle;
    const fullDescription = description || seoConfig.defaultDescription;
    const fullImage = image || seoConfig.defaultImage;
    const fullCanonical = `${seoConfig.siteUrl}${canonical || ''}`;
    
    return (
        <Helmet>
            {/* Primary Meta Tags */}
            <title>{fullTitle}</title>
            <meta name="title" content={fullTitle} />
            <meta name="description" content={fullDescription} />
            {keywords && <meta name="keywords" content={keywords} />}
            <link rel="canonical" href={fullCanonical} />
            {noindex && <meta name="robots" content="noindex, nofollow" />}
            
            {/* Open Graph / Facebook */}
            <meta property="og:type" content={type} />
            <meta property="og:url" content={fullCanonical} />
            <meta property="og:title" content={fullTitle} />
            <meta property="og:description" content={fullDescription} />
            <meta property="og:image" content={fullImage} />
            <meta property="og:site_name" content={seoConfig.siteName} />
            <meta property="og:locale" content={seoConfig.locale} />
            
            {/* Twitter */}
            <meta name="twitter:card" content="summary_large_image" />
            <meta name="twitter:url" content={fullCanonical} />
            <meta name="twitter:title" content={fullTitle} />
            <meta name="twitter:description" content={fullDescription} />
            <meta name="twitter:image" content={fullImage} />
            
            {/* Schema.org structured data */}
            {schema && (
                <script type="application/ld+json">
                    {JSON.stringify(schema)}
                </script>
            )}
        </Helmet>
    );
};

export default SEOHead;
